import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Field, Input, Toggle } from '../../../components/ui';
import { departmentsApi, type DepartmentDetail } from '../../../lib/departments';
import { getThemeColors } from '../../../lib/theme';
import { apiErrorMessage, groupKey } from './shared';

interface Props {
  dark: boolean;
  dept: DepartmentDetail;
  onSaved: () => void | Promise<void>;
}

export function DepartmentGeneralTab({ dark, dept, onSaved }: Props) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const [name, setName] = useState(dept.name);
  const [color, setColor] = useState(dept.color);
  const [description, setDescription] = useState(dept.description ?? '');
  const [active, setActive] = useState(dept.is_active);
  const [saving, setSaving] = useState(false);
  const [state, setState] = useState<{ kind: 'idle' | 'ok' | 'err'; msg?: string }>({ kind: 'idle' });

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    setState({ kind: 'idle' });
    try {
      await departmentsApi.update(dept.id, { name: name.trim(), color, description: description.trim() || null, is_active: active });
      setState({ kind: 'ok' });
      await onSaved();
    } catch (e) {
      setState({ kind: 'err', msg: apiErrorMessage(e, tr('departments.general.save_error')) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card dark={dark} style={{ padding: 20 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Field label={tr('departments.fields.name')}>
          <Input dark={dark} aria-label={tr('departments.fields.name')} value={name} onChange={e => setName(e.target.value)} />
        </Field>
        <Field label={tr('departments.fields.slug')} hint={tr('departments.general.slug_fixed')}>
          <Input dark={dark} aria-label={tr('departments.fields.slug')} value={dept.slug} disabled readOnly />
        </Field>
        <Field label={tr('departments.fields.color')}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <input
              type="color"
              aria-label={tr('departments.fields.color')}
              value={color}
              onChange={e => setColor(e.target.value)}
              style={{ width: 40, height: 34, border: 'none', background: 'transparent', cursor: 'pointer' }}
            />
            <span style={{ fontSize: 12, color: t.muted }}>{color}</span>
          </div>
        </Field>
        <Field label={tr('departments.fields.group')} hint={tr('departments.fields.group_fixed')}>
          <Input dark={dark} aria-label={tr('departments.fields.group')} value={tr(`departments.group.${groupKey(dept.user_role)}_card`)} disabled readOnly />
        </Field>
      </div>
      <div style={{ marginTop: 14 }}>
        <Field label={tr('departments.fields.description')}>
          <Input dark={dark} aria-label={tr('departments.fields.description')} value={description} onChange={e => setDescription(e.target.value)} />
        </Field>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14 }}>
        <Toggle checked={active} onChange={setActive} />
        <span style={{ fontSize: 13, color: t.text }}>{tr('departments.general.active_label')}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 20 }}>
        <Button dark={dark} variant="primary" disabled={saving || !name.trim()} onClick={() => void save()}>
          {tr('departments.common.save')}
        </Button>
        {state.kind === 'ok' && <span style={{ fontSize: 12, color: '#027a48' }}>{tr('departments.common.saved')}</span>}
        {state.kind === 'err' && <span role="alert" style={{ fontSize: 12, color: '#b42318' }}>{state.msg}</span>}
      </div>
    </Card>
  );
}
